import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DEANProvider } from './contexts/DEANContext';
import { NotesWindowProvider } from './contexts/NotesWindowContext';
import { DEANPanel } from './components/staff/DEANPanel';
import { FloatingNotesWindow } from './components/staff/FloatingNotesWindow';
import { LandingPage } from './components/LandingPage';
import { LoginPage } from './components/auth/LoginPage';
import { UserOnboarding } from './components/auth/UserOnboarding';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { ProtectedRoute } from './components/ProtectedRoute';
import StaffDashboard from './components/staff/StaffDashboard';
import ClientDashboard from './components/client/ClientDashboard';
import { BookingPage } from './components/public/BookingPage';

function AppWrapper() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingEmail, setOnboardingEmail] = useState('');

  const handleStartOnboarding = (email: string) => {
    setOnboardingEmail(email);
    setShowOnboarding(true);
  };

  const handleCancelOnboarding = () => {
    setShowOnboarding(false);
    setOnboardingEmail('');
  };

  if (showOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-content-center">
        <div className="text-center">
          <h2>Onboarding Flow Coming Soon</h2>
          <p>Email: {onboardingEmail}</p>
          <button onClick={handleCancelOnboarding}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/" 
          element={
            <LandingPage
              onNavigateToLogin={() => window.location.href = '/login/client'}
              onStartOnboarding={handleStartOnboarding}
            />
          } 
        />
        {/* Authentication Routes */}
        <Route path="/login/client" element={<LoginPage context="client" />} />
        <Route path="/login/staff" element={<LoginPage context="staff" />} />
        <Route path="/login/admin" element={<LoginPage context="admin" />} />
        
        {/* Legacy redirects for backward compatibility */}
        <Route path="/client" element={<Navigate to="/login/client" replace />} />
        <Route path="/staff" element={<Navigate to="/login/staff" replace />} />
        <Route path="/admin" element={<Navigate to="/login/admin" replace />} />
        
        {/* Admin Context Routes */}
        <Route path="/admin/*" element={
          <ProtectedRoute requiredContext="admin">
            <AdminDashboard />
          </ProtectedRoute>
        } />
        
        {/* Staff Context Routes */}
        <Route path="/staff/*" element={
          <ProtectedRoute requiredContext="staff">
            <StaffDashboard />
          </ProtectedRoute>
        } />
        
        {/* Client Context Routes */}
        <Route path="/client/*" element={
          <ProtectedRoute requiredContext="client">
            <ClientDashboard />
          </ProtectedRoute>
        } />
        
        <Route
          path="/onboarding"
          element={<UserOnboarding />}
        />

        {/* Public booking page (no auth required) */}
        <Route path="/book" element={<BookingPage />} />
        
        {/* Legacy dashboard redirect */}
        <Route path="/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
        
        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <DEANProvider>
            <NotesWindowProvider>
              <AppWrapper />
              <DEANPanel />
              <FloatingNotesWindow />
              <Toaster position="bottom-right" toastOptions={{ duration: 4000 }} />
            </NotesWindowProvider>
          </DEANProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;